import { useCallback, useMemo, useRef, useState } from "react";
import SelectorDeDia from "./SelectorDeDia";
import { useTranslation } from "react-i18next";

import { useDescartarMedia } from "../../../features/centinela/mutations";

import type { CentinelaCaseMedia } from "../../../features/centinela/types";
import CaseDashcamShots from "./CaseDashcamShots";
import CentinelaPensando, { FRASES_EVIDENCIA } from "./CentinelaPensando";

/**
 * Todo lo que grabó la cámara.
 *
 * ── Por qué es su propia pestaña ────────────────────────────────────────────
 * Estaba al final de Conducción, y ahí competía por espacio con los números:
 * para llegar a una foto había que pasar por ocho indicadores y una regla. Son
 * dos cosas que se buscan en momentos distintos —uno entra a Conducción a saber
 * cómo le fue, y a Evidencias a VER algo concreto— y ninguna de las dos se
 * beneficiaba de estar debajo de la otra.
 *
 * ── Por qué ésta sí es condicional y Conducción no ──────────────────────────
 * Una unidad sin cámara no tiene evidencia que enseñar: la pestaña estaría
 * siempre vacía y sería una promesa incumplida en la barra. Conducción, en
 * cambio, sigue teniendo kilómetros, tiempos, movimientos e incidencias, y por
 * eso nunca se oculta.
 */

export interface EvidenciasPanelProps {
  deviceId: string | null;
  /** Lo que grabó la cámara en las últimas horas, haya caso o no. */
  unitMedia: CentinelaCaseMedia[];
  unitMediaLoading?: boolean;
  /** El día que se está mirando, en `YYYY-MM-DD`. */
  dia?: string;
  onDia?: (fecha: string) => void;
  /**
   * Cuándo subió algo esta cámara por última vez, mire quien mire qué día.
   *
   * Es lo que separa «este día no pasó nada» de «la cámara lleva una semana
   * muda», que es justo la confusión que el vacío provocaba antes.
   */
  ultima?: { fecha: string; ts: number | null; archivos: number } | null;
  /*
    La evidencia del CASO abierto ya no se dibuja aquí: vive completa en la
    tarjeta del caso, que es donde se va a atenderlo. Dejar las props era peor
    que quitarlas —el panel seguía ESPERANDO por una consulta cuyo resultado
    no enseña, y esa espera se la comía el usuario cada vez que abría la
    pestaña—. Ver la nota del carrusel más abajo.
  */
  openCaseId: string | null;
}

export default function EvidenciasPanel({
  deviceId,
  unitMedia,
  unitMediaLoading,
  openCaseId,
  dia,
  onDia,
  ultima = null,
}: EvidenciasPanelProps) {
  const { t, i18n } = useTranslation("common");

  // Mientras no hay nada que enseñar, Centinela dice en qué va. Las frases son
  // las de la evidencia —buscar en el bucket, descartar las inservibles, firmar
  // los enlaces—, no las del parte: un loader que anuncia un trabajo distinto
  // del que hace es peor que uno mudo.
  //
  // ── Se espera a la consulta, traiga algo o no ──────────────────────────
  //
  // Antes esto además exigía que las listas estuvieran vacías. Con eso, en
  // cuanto llegaba algo el loader se apagaba y el carrusel tomaba el relevo
  // con su propio «Buscando capturas…» en texto plano: el avatar desaparecía
  // a media espera y lo sustituía una línea gris, que es justo lo que parece
  // un error.
  //
  // Ahora el avatar se queda hasta que la consulta termina. Es `isLoading`, o
  // sea sólo la PRIMERA carga: un refresco de fondo no vacía la pantalla de lo
  // que ya se estaba viendo.
  const cargando = unitMediaLoading;

  const descartar = useDescartarMedia(deviceId);

  /*
    ── Qué pasa al tocar la basurita ──────────────────────────────────────────
    Tres cosas, y las tres hacen falta:

      1. La miniatura se desvanece EN EL SITIO, antes de que el servidor
         conteste. Sin eso, el clic no se siente: la lista se recarga medio
         segundo después y el archivo desaparece de golpe, que se lee como un
         parpadeo de la página y no como «lo archivé».

      2. Aparece una franja con «Descartada» y un DESHACER. Descartar es una
         opinión y las opiniones se corrigen en el momento, no bajando a abrir
         el plegado de abajo.

      3. Si el servidor falla, la miniatura VUELVE y se dice. Un desvanecido
         que no corresponde a nada guardado es peor que no animar: enseña a
         confiar en una señal falsa.
  */
  const [saliendo, setSaliendo] = useState<Set<string>>(new Set());
  const [aviso, setAviso] = useState<{ object: string; error?: boolean } | null>(null);
  const relojAviso = useRef<ReturnType<typeof setTimeout> | null>(null);

  const quitarDeSalida = useCallback((object: string) => {
    setSaliendo((prev) => {
      const siguiente = new Set(prev);
      siguiente.delete(object);
      return siguiente;
    });
  }, []);

  const mostrarAviso = useCallback((valor: { object: string; error?: boolean }) => {
    if (relojAviso.current) clearTimeout(relojAviso.current);
    setAviso(valor);
    // Seis segundos: lo que tarda alguien en darse cuenta de que se equivocó y
    // buscar el deshacer. Menos y hay que ir a buscarlo al plegado.
    relojAviso.current = setTimeout(() => setAviso(null), 6000);
  }, []);

  const onDescartar = useCallback(
    (object: string) => {
      setSaliendo((prev) => new Set(prev).add(object));
      descartar.mutate(
        { object },
        {
          onSuccess: () => mostrarAviso({ object }),
          onError: () => {
            quitarDeSalida(object);
            mostrarAviso({ object, error: true });
          },
        },
      );
    },
    [descartar, mostrarAviso, quitarDeSalida],
  );

  const onRecuperar = useCallback(
    (object: string) => {
      if (relojAviso.current) clearTimeout(relojAviso.current);
      setAviso(null);
      quitarDeSalida(object);
      descartar.mutate({ object, descartar: false });
    },
    [descartar, quitarDeSalida],
  );

  /*
    ── Tres montones, no uno ──────────────────────────────────────────────────
    Lo bueno arriba, lo dudoso aparte y lo descartado plegado. Mezclados, había
    que abrir treinta miniaturas para encontrar las dos que servían; y esconder
    lo dudoso perdía un dato real —que la cámara de cabina suba su pantalla de
    arranque significa que perdió alimentación—.
  */
  const { buenas, sospechosas, descartadas } = useMemo(() => {
    const b: typeof unitMedia = [];
    const s: typeof unitMedia = [];
    const d: typeof unitMedia = [];
    for (const m of unitMedia) {
      if (m.descartada) d.push(m);
      else if (m.util === false) s.push(m);
      else b.push(m);
    }
    return { buenas: b, sospechosas: s, descartadas: d };
  }, [unitMedia]);

  if (cargando) return <CentinelaPensando frases={FRASES_EVIDENCIA} />;

  return (
    <div className="space-y-4">
      {/*
        El selector queda montado aunque el día venga vacío: si desapareciera
        con el vacío, no habría forma de moverse a otro día — que es
        exactamente lo que hace falta cuando no hay nada.
      */}
      {onDia && dia && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <SelectorDeDia dia={dia} onDia={onDia} />
          <span className="font-mono text-[10px] max-sm:text-[11px] text-content-faint tabular-nums">
            {t("monitoreoIa.evidencias.archivosDelDia", {
              defaultValue: "{{count}} archivos",
              count: unitMedia.length,
            })}
          </span>
        </div>
      )}
      {/* La del caso abierto va primero: es la que alguien está atendiendo
          ahora mismo, y buscarla dentro del carrusel general sería absurdo. */}
      {/* Nada encima del carrusel. La evidencia del caso abierto vivía aquí
          arriba y sobraba por dos motivos: ya se ve completa en la tarjeta del
          caso —que es donde se va a atenderlo— y aquí obligaba a pasar por ella
          para llegar a lo que uno viene a buscar, que es todo lo que grabó la
          cámara. Un rótulo que separa dos galerías es la solución al problema
          de tener dos galerías; quitar una es mejor. */}
      {/* Y todo lo demás, sin discriminar por caso: los hechos de solo registro
          piden foto y NUNCA abren caso, así que sin este bloque esas imágenes
          llegan al bucket y nadie las ve. */}
      <CaseDashcamShots
        media={buenas}
        isLoading={unitMediaLoading}
        titleKey=""
        carrusel
      />

      {/* Las sospechosas, en el MISMO carrusel que el resto: mismas
          miniaturas, mismos colores, mismas flechas. Lo único que cambia es la
          basurita sobre cada una. Se sigue enseñando cuando ya no queda
          ninguna, porque desaparecer el bloque entero deja al usuario sin saber
          si descartó todo o si algo se rompió. */}
      {(sospechosas.length > 0 || descartadas.length > 0) && (
        <div className="rounded-xl border border-dashed border-warning/40 p-3">
          <p className="mb-1 text-sm font-semibold text-content">
            {t("monitoreoIa.evidencias.sospechosasTitulo", {
              defaultValue: "Posible evidencia sin imagen",
            })}
          </p>
          {/* Se explica la sospecha Y su límite: el sistema no distingue una
              pantalla de arranque de una noche muy cerrada. Por eso señala y
              no borra. */}
          <p className="mb-2 text-[10px] max-sm:text-[11px] leading-relaxed text-content-faint">
            {t("monitoreoIa.evidencias.sospechosasNota", {
              defaultValue:
                "Pesan muy poco para ser una escena: casi siempre es la pantalla de arranque de la cámara de cabina. Pero una noche cerrada o una lente empañada también pesan poco, así que revísalas antes de descartar. Descartar no borra el archivo.",
            })}
          </p>

          <CaseDashcamShots
            media={sospechosas}
            carrusel
            titleKey=""
            vacioKey="monitoreoIa.evidencias.nadaQueDescartar"
            onDescartar={(m) => m.object && onDescartar(m.object)}
            descartando={descartar.isPending}
            saliendo={(m) => Boolean(m.object && saliendo.has(m.object))}
          />

          {aviso && (
            <div
              role="status"
              className={`mt-2 flex items-center justify-between gap-2 rounded-lg px-2.5 py-1.5 text-[11px] ${
                aviso.error
                  ? "bg-error/10 text-error"
                  : "bg-success/10 text-success"
              }`}
            >
              <span className="flex min-w-0 items-center gap-1.5">
                <i
                  className={aviso.error ? "ri-error-warning-line" : "ri-check-line"}
                  aria-hidden="true"
                />
                <span className="truncate">
                  {aviso.error
                    ? t("monitoreoIa.evidencias.descarteFallo", {
                        defaultValue: "No se pudo descartar. Sigue en la lista.",
                      })
                    : t("monitoreoIa.evidencias.descartada", {
                        defaultValue: "Descartada. El archivo no se borró.",
                      })}
                </span>
              </span>
              {!aviso.error && (
                <button
                  type="button"
                  onClick={() => onRecuperar(aviso.object)}
                  className="shrink-0 font-medium underline underline-offset-2"
                >
                  {t("monitoreoIa.evidencias.deshacer", { defaultValue: "Deshacer" })}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {descartadas.length > 0 && (
        <details className="rounded-xl bg-surface px-3 py-2">
          <summary className="cursor-pointer text-xs text-content-faint">
            {t("monitoreoIa.evidencias.descartadas", {
              defaultValue: "{{n}} descartadas",
              n: descartadas.length,
            })}
          </summary>
          {/* Deshacer tiene que estar a la vista: descartar es una opinión y
              las opiniones se corrigen. El archivo nunca se borró. */}
          <ul className="mt-2 space-y-1">
            {descartadas.map((m) => (
              <li
                key={m.object || m.ts}
                className="flex items-center justify-between gap-2 text-[11px] text-content-faint"
              >
                <span className="truncate">
                  {new Date(m.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} ·{" "}
                  {m.kind === "video" ? "video" : "foto"}
                </span>
                <button
                  type="button"
                  disabled={!m.object || descartar.isPending}
                  onClick={() => m.object && onRecuperar(m.object)}
                  className="shrink-0 rounded px-1.5 py-0.5 text-[10px] max-sm:px-2 max-sm:py-1.5 max-sm:text-[11px] text-info transition-colors hover:bg-info/10 disabled:opacity-40"
                >
                  {t("monitoreoIa.evidencias.recuperar", { defaultValue: "Recuperar" })}
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}

      {/*
        ── El vacío dice CUÁNDO fue la última vez ──────────────────────────
        Antes decía sólo «la cámara no ha subido nada en las últimas horas», y
        con eso no se puede distinguir un día tranquilo de una cámara rota.
        Pasó de verdad: la cámara de la Cherokee subió 166 archivos un jueves,
        dejó de hablar a las 8:43, y a la mañana siguiente la pestaña daba a
        entender que nunca había grabado nada.
        Ahora el vacío afirma dos cosas y las separa: qué no hay HOY, y cuándo
        hubo algo por última vez. La segunda es la que dice si hay que ir a
        revisar el equipo.
      */}
      {!openCaseId && unitMedia.length === 0 && !unitMediaLoading && (
        <div className="space-y-1 rounded-xl bg-surface px-3 py-2">
          <p className="text-xs text-content-faint">
            {dia
              ? t("monitoreoIa.evidencias.vacioDia", {
                  defaultValue: "Sin evidencia el {{dia}}.",
                  dia: new Date(`${dia}T12:00:00`).toLocaleDateString(i18n.language, {
                    day: "numeric",
                    month: "long",
                  }),
                })
              : t("monitoreoIa.evidencias.vacio", {
                  defaultValue: "La cámara no ha subido nada en las últimas horas.",
                })}
          </p>

          {ultima?.ts ? (
            <p className="text-[11px] text-content-secondary">
              {t("monitoreoIa.evidencias.ultimaVez", {
                defaultValue: "La última vez que subió algo fue el {{cuando}}.",
                cuando: new Date(ultima.ts).toLocaleString(i18n.language, {
                  day: "numeric",
                  month: "long",
                  hour: "2-digit",
                  minute: "2-digit",
                }),
              })}
            </p>
          ) : (
            <p className="text-[11px] text-content-secondary">
              {t("monitoreoIa.evidencias.nuncaSubio", {
                defaultValue: "Esta cámara no ha subido nada nunca.",
              })}
            </p>
          )}

          <p className="text-[11px] text-content-faint">
            {t("monitoreoIa.evidencias.pedirla", {
              defaultValue: "Puedes pedirle una foto o un clip desde la pestaña de cámara.",
            })}
          </p>
        </div>
      )}
    </div>
  );
}
